<template>
    <div data-testid="step-addressDetails" class="container">
        <h1 class="container__standard-header">
            {{ $t(`${TRANSLATION_KEY}.addressHeader`) }}
        </h1>
        <form @submit="blockSendForm">
            <div class="row custom-select-wrapper">
                <TheCountrySelect
                    name="country"
                    :default-value="defaultCountry"
                    :country-list="countries"
                    :label="$t(`${TRANSLATION_KEY}.homeCountry`)"
                    :placeholder="
                        $t(`${TRANSLATION_KEY}.chooseCountryPlaceholder`)
                    "
                    class="custom-select"
                    :country-disable="isDefaultCountrySelected"
                    @selected-country="formData.country = $event"
                />
            </div>
            <div class="row">
                <div class="search-instructions">
                    {{ $t(`${TRANSLATION_KEY}.searchAddress`) }}
                </div>
                <Field
                    key="searchAddressOther"
                    v-model="formData.searchAddress"
                    type="text"
                    name="searchAddress"
                    :placeholder="
                        $t(`${TRANSLATION_KEY}.searchAddressPlaceholder`)
                    "
                    cy-data="searchAddress"
                    :is-validated="false"
                    :street-filter="true"
                    @focus="populateDataForm"
                />
            </div>
            <div class="row">
                <Field
                    key="postalCodeOther"
                    v-model="formData.postalCode"
                    type="text"
                    name="postalCode"
                    :placeholder="$t(`${TRANSLATION_KEY}.postcodePlaceholder`)"
                    cy-data="zip"
                    @keyup="keyupEvent"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.postcode`) }}
                    </span>
                </Field>
            </div>
            <div class="row">
                <Field
                    v-model="formData.city"
                    type="text"
                    name="city"
                    :placeholder="$t(`${TRANSLATION_KEY}.cityPlaceholder`)"
                    cy-data="city"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.city`) }}
                    </span>
                </Field>
            </div>
            <div class="row">
                <Field
                    v-model="formData.street"
                    type="text"
                    name="street"
                    :placeholder="$t(`${TRANSLATION_KEY}.streetPlaceholder`)"
                    cy-data="street"
                    :street-filter="true"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.street`) }}
                    </span>
                </Field>
            </div>
            <div class="row row--numbers-of-house-flat">
                <Field
                    v-model="formData.buildingNumber"
                    type="text"
                    name="buildingNumber"
                    :placeholder="
                        $t(`${TRANSLATION_KEY}.houseNumberPlaceholder`)
                    "
                    cy-data="building-number"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.houseNumberPlaceholder`) }}
                    </span>
                </Field>
            </div>
            <div class="row row--numbers-of-house-flat">
                <Field
                    v-model="formData.apartmentNumber"
                    type="text"
                    name="apartmentNumber"
                    :placeholder="
                        $t(`${TRANSLATION_KEY}.flatNumberPlaceholder`)
                    "
                    cy-data="apartment-number"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.flatNumberPlaceholder`) }}
                    </span>
                </Field>
            </div>
            <div class="row custom-select-wrapper row--correspondence-method">
                <Select
                    ref-value="correspondenceMethod"
                    :item-list="correspondenceMethodArray"
                    :default-value="String(CorrespondenceMethod.EMAIL)"
                    :model-value="formData.correspondenceChoice"
                    :placeholder="
                        $t(`${TRANSLATION_KEY}.chooseCorrespondenceMethod`)
                    "
                    :label="$t(`${TRANSLATION_KEY}.correspondenceMethod`)"
                    name="correspondenceChoice"
                    cy-data="correspondence-choice"
                    @update:model-value="formData.correspondenceChoice = $event"
                />
            </div>
            <div
                v-if="isPaperCorrespondenceSelected"
                class="row correspondence-info"
            >
                <p>
                    {{ $t(`${TRANSLATION_KEY}.correspondenceMethodTwoLabel1`) }}
                </p>
                <p>
                    {{ $t(`${TRANSLATION_KEY}.correspondenceMethodTwoLabel2`) }}
                </p>
            </div>
            <div
                v-if="isPaperCorrespondenceSelected"
                class="row row-feeds-and-commisions"
            >
                <a
                    :href="$t(`${TRANSLATION_KEY}.feesAndCommisionsTableFile`)"
                    rel="noopener"
                    target="_blank"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.feesAndCommisionsTable`) }}
                    </span>
                </a>
            </div>
            <div
                v-if="isPaperCorrespondenceSelected"
                class="row row--address-for-correspondence"
            >
                <CheckBox
                    v-model="formData.contactAddress"
                    name="contactAddress"
                    cy-data="has-different-contact-address"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.correspondenceAddress`) }}
                    </span>
                </CheckBox>
            </div>
            <transition name="smooth">
                <div
                    v-if="
                        isPaperCorrespondenceSelected && formData.contactAddress
                    "
                    class="row row--information-about-correspondence-address"
                    :style="{ maxHeight: maxCorrespondenceHeight + 'px' }"
                >
                    <div class="row custom-select-wrapper">
                        <Select
                            :item-list="countries"
                            name="contactCountry"
                            :placeholder="
                                $t(
                                    `${TRANSLATION_KEY}.chooseCountryPlaceholder`
                                )
                            "
                            :label="$t(`${TRANSLATION_KEY}.homeCountry`)"
                            cy-data="contact-country"
                            :flags="true"
                            @update:model-value="
                                formData.contactCountry = $event
                            "
                        />
                    </div>
                    <div class="row">
                        <Field
                            v-if="formData.contactCountry !== 'PL'"
                            key="contactPostalCodeOther"
                            v-model="formData.contactPostalCode"
                            type="text"
                            name="contactPostalCode"
                            :placeholder="
                                $t(
                                    `${TRANSLATION_KEY}.correspondencePostcodePlaceholder`
                                )
                            "
                            cy-data="contact-zip"
                        >
                            <span>
                                {{
                                    $t(
                                        `${TRANSLATION_KEY}.correspondencePostcode`
                                    )
                                }}
                            </span>
                        </Field>
                        <!-- if country === PL -->
                        <FieldMask
                            v-else
                            key="contactPostalCodePL"
                            v-model="formData.contactPostalCode"
                            type="text"
                            name="contactPostalCode"
                            pattern="##-###"
                            :placeholder="
                                $t(
                                    `${TRANSLATION_KEY}.correspondencePostcodePlaceholder`
                                )
                            "
                            cy-data="zip"
                            @blur="requestCorrespondenceCity($event)"
                        >
                            <span>
                                {{
                                    $t(
                                        `${TRANSLATION_KEY}.correspondencePostcode`
                                    )
                                }}
                            </span>
                        </FieldMask>
                    </div>
                    <div class="row">
                        <AutocompleteField
                            v-model="formData.contactCity"
                            type="text"
                            name="contactCity"
                            :placeholder="
                                $t(
                                    `${TRANSLATION_KEY}.correspondenceCityPlaceholder`
                                )
                            "
                            cy-data="contact-city"
                            :item-list="correspondenceCityItemList"
                            :override-old-value="true"
                            @blur="getCorrespondenceStreets()"
                        >
                            <span>
                                {{
                                    $t(`${TRANSLATION_KEY}.correspondenceCity`)
                                }}
                            </span>
                        </AutocompleteField>
                    </div>
                    <div class="row">
                        <AutocompleteField
                            v-model="formData.contactStreet"
                            type="text"
                            name="contactStreet"
                            :placeholder="
                                $t(
                                    `${TRANSLATION_KEY}.correspondenceStreetPlaceholder`
                                )
                            "
                            cy-data="contact-street"
                            :item-list="correspondenceStreetItemList"
                            :is-street="true"
                        >
                            <span>
                                {{
                                    $t(
                                        `${TRANSLATION_KEY}.correspondenceStreet`
                                    )
                                }}
                            </span>
                        </AutocompleteField>
                    </div>
                    <div class="row row__correspondence-information-house">
                        <Field
                            v-model="formData.contactBuildingNumber"
                            type="text"
                            name="contactBuildingNumber"
                            :placeholder="
                                $t(`${TRANSLATION_KEY}.houseNumberPlaceholder`)
                            "
                            cy-data="contact-building-number"
                        >
                            <span>
                                {{
                                    $t(
                                        `${TRANSLATION_KEY}.houseNumberPlaceholder`
                                    )
                                }}
                            </span>
                        </Field>
                    </div>
                    <div class="row row__correspondence-information-house">
                        <Field
                            v-model="formData.contactApartmentNumber"
                            type="text"
                            name="contactApartmentNumber"
                            :placeholder="
                                $t(`${TRANSLATION_KEY}.flatNumberPlaceholder`)
                            "
                            cy-data="contact-apartment-number"
                        >
                            <span>
                                {{
                                    $t(
                                        `${TRANSLATION_KEY}.flatNumberPlaceholder`
                                    )
                                }}
                            </span>
                        </Field>
                    </div>
                </div>
            </transition>
            <SubmitButton
                :action="Action.ADDRESS_DETAILS"
                :button-value="$t(`${TRANSLATION_KEY}.next`)"
                :data="formData"
                data-testid="btn-addressDetails-nextStep"
                class="button button--submit"
                :disabled="
                    !(formData.country &&
                    formData.postalCode &&
                    formData.city &&
                    formData.street &&
                    formData.buildingNumber &&
                    formData.contactAddress === false
                        ? true
                        : formData.contactCountry &&
                          formData.contactPostalCode &&
                          formData.contactCity &&
                          formData.contactStreet &&
                          formData.contactBuildingNumber)
                "
                @click="
                    () =>
                        amplitudeSendEvent(
                            AmplitudeAnalyticsEvents.address.ADDRESS_CONTINUE
                        )
                "
            />
        </form>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    ref,
    reactive,
    onMounted,
    watch,
    computed,
} from 'vue';
import { useStore } from 'vuex';

import CheckBox from '@/components/basic/fields/CheckBox.vue';
import Field from '@/components/basic/fields/Field.vue';
import FieldMask from '../basic/fields/FieldMask.vue';
import Select from '@/components/basic/fields/Select.vue';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import AutocompleteField from '../compositeFields/AutocompleteField.vue';
import TheCountrySelect from '@/components/basic/fields/TheCountrySelect.vue';

import { CorrespondenceMethod } from '@/constants/form-values';
import { SET_PROGRESS } from '@/constants/mutation-types';
import { Action } from '@/constants/ajax';

import type {
    AddressResponseInterface,
    DataObject,
    FieldInterface,
} from '@/types';
import { useI18n } from 'vue-i18n';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';

export default defineComponent({
    name: 'AddressDetails',
    components: {
        CheckBox,
        Select,
        Field,
        FieldMask,
        SubmitButton,
        TheCountrySelect,
        AutocompleteField,
    },
    setup() {
        const store = useStore();
        const { t } = useI18n();

        const TRANSLATION_KEY = 'steps.AddressDetails';
        const created = ref(false);
        const isFilled = ref(false);
        const maxCorrespondenceHeight = ref(420);

        const formData = reactive({
            country: '',
            postalCode: '',
            searchAddress: '',
            city: '',
            street: '',
            buildingNumber: '',
            apartmentNumber: '',
            correspondenceChoice: String(CorrespondenceMethod.EMAIL),
            contactAddress: false,
            contactCountry: '',
            contactPostalCode: '',
            contactCity: '',
            contactStreet: '',
            contactBuildingNumber: '',
            contactApartmentNumber: '',
        });

        const cityItemList = ref<FieldInterface[]>([]);
        const streetItemList = ref<FieldInterface[]>([]);
        const correspondenceCityItemList = ref<FieldInterface[]>([]);
        const correspondenceStreetItemList = ref<FieldInterface[]>([]);
        const cityStreetMap = ref<DataObject>({});
        const correspondeneCityStreetMap = ref<DataObject>({});

        const CorrespondenceMethodRef = CorrespondenceMethod;
        const correspondenceMethodArray = computed(() => [
            {
                key: CorrespondenceMethod.EMAIL,
                value: t('steps.AddressDetails.correspondenceMethodOne'),
            },
            {
                key: CorrespondenceMethod.POST_OFFICE,
                value: t('steps.AddressDetails.correspondenceMethodTwo'),
            },
        ]);
        const isPaperCorrespondenceSelected = computed(
            () =>
                String(formData.correspondenceChoice) ===
                String(CorrespondenceMethod.POST_OFFICE)
        );

        const countries = computed((): FieldInterface[] => {
            return store.state.config.countries || [];
        });
        const defaultCountry = computed(() =>
            String(store.state.formData.country || '')
        );
        const isDefaultCountrySelected = computed(() =>
            Boolean(store.state.formData.country)
        );
        onMounted(() => {
            created.value = true;
            if (store.state.timeToFinish >= 6) {
                store.commit(SET_PROGRESS, PROGRESS_BAR_TIMES.ADDRESS_DETAILS);
            }
        });

        watch(
            () => formData.country,
            (newVal) => {
                if (newVal === 'PL' && formData.postalCode.length === 6) {
                    requestCity(formData.postalCode);
                }
                store.state.country = newVal;
            }
        );

        watch(
            () => formData.postalCode,
            (newVal) => {
                if (formData.country === 'PL' && newVal.length === 6) {
                    requestCity(newVal);
                }
            }
        );

        watch(
            () => formData.contactAddress,
            (val) => {
                if (val) {
                } else {
                    formData.contactCountry = '';
                    formData.contactPostalCode = '';
                    formData.contactCity = '';
                    formData.contactStreet = '';
                    formData.contactBuildingNumber = '';
                    formData.contactApartmentNumber = '';
                }
            }
        );

        watch(
            () => formData.correspondenceChoice,
            (value) => {
                if (
                    String(value) !== String(CorrespondenceMethod.POST_OFFICE)
                ) {
                    formData.contactAddress = false;
                }
            }
        );

        watch(
            () => formData.contactCountry,
            (newVal, oldVal) => {
                if (oldVal === 'PL' && newVal !== 'PL') {
                    correspondenceCityItemList.value = [];
                    correspondenceStreetItemList.value = [];
                }

                if (
                    oldVal !== 'PL' &&
                    newVal === 'PL' &&
                    formData.contactPostalCode.length === 6
                ) {
                    requestCorrespondenceCity(formData.contactPostalCode);
                }
            }
        );

        watch(
            () => formData.contactCity,
            () => {
                getCorrespondenceStreets();
            }
        );

        function blockSendForm(evt: Event) {
            evt.preventDefault();
        }

        function keyupEvent() {
            window.addEventListener('keyup', (evt: KeyboardEvent) => {
                if (evt.key === 'Enter') {
                    if (!isFilled.value) {
                        isFilled.value = true;
                    }
                }
            });
        }

        function expandForm(): boolean {
            isFilled.value = !isFilled.value;
            return isFilled.value;
        }

        function getItemListFromStringArray(array: string[]): FieldInterface[] {
            return array.map((elem, index) => ({ key: index, value: elem }));
        }

        async function getCityList(postalCode: string) {
            try {
                const promise = store.state.ajaxService.runAction(
                    Action.GET_ADDRESS,
                    {
                        postalCode,
                    }
                );
                const data: AddressResponseInterface = await promise;

                const response = {
                    cities: [] as string[],
                    streets: new Map<string, string[]>(),
                };

                const map = new Map<string, string[]>();
                Object.entries(data.postalCodeAddresses).forEach(
                    ([key, value]) => {
                        response.cities.push(key);
                        map.set(key, value as string[]);
                    }
                );
                response.streets = map;
                return response;
            } catch (err) {
                console.error(err);
            }
        }

        async function requestCity(postalCode: string) {
            const resp = await getCityList(postalCode);
            if (!resp) return;

            cityStreetMap.value = resp;
            cityItemList.value = getItemListFromStringArray(resp.cities);
        }

        function getCityStreets() {
            if (formData.city) {
                const streetArray = cityStreetMap.value.streets?.get(
                    formData.city
                );
                if (streetArray) {
                    streetArray.sort();
                    streetItemList.value =
                        getItemListFromStringArray(streetArray);
                }
            }
            return streetItemList.value;
        }

        async function requestCorrespondenceCity(postalCode: string) {
            const resp = await getCityList(postalCode);
            if (!resp) return;

            correspondeneCityStreetMap.value = resp;

            const cities =
                Array.from(correspondeneCityStreetMap.value['cities']) || [];

            if (!cities.length) {
                correspondenceCityItemList.value = [];
                correspondenceStreetItemList.value = [];
                return;
            }
            correspondenceCityItemList.value = getItemListFromStringArray(
                cities.filter(
                    (city): city is string => typeof city === 'string'
                )
            );
        }

        function getCorrespondenceStreets() {
            if (formData.contactCountry !== 'PL') {
                correspondenceStreetItemList.value = [];
                return;
            }

            if (
                formData.contactCity &&
                correspondeneCityStreetMap.value['streets']
            ) {
                const streetArray = correspondeneCityStreetMap.value[
                    'streets'
                ].get(formData.contactCity);

                if (Array.isArray(streetArray)) {
                    streetArray.sort();
                    correspondenceStreetItemList.value =
                        getItemListFromStringArray(streetArray);
                } else {
                    correspondenceStreetItemList.value = [];
                }
            } else {
                correspondenceStreetItemList.value = [];
            }
            return correspondenceStreetItemList.value;
        }

        function populateDataForm() {
            const countryCodes = store.state.formData.country || '';
            const options = {
                countries: { codesList: countryCodes },
                setCountryByIP: false,
            };
            const fields = [
                { element: 'searchAddress', field: 'searchAddress' },
            ];

            const pcaApi = (window as Window & { pca?: any }).pca;
            if (!pcaApi || typeof pcaApi.Address !== 'function') {
                return;
            }
            const addressControl = new pcaApi.Address(fields, options);
            addressControl.load();
            addressControl.listen('populate', (address: any) => {
                formData.postalCode = address.PostalCode;
                formData.city = address.City;
                formData.street = address.Street;
                formData.buildingNumber = address.BuildingNumber;
                formData.apartmentNumber = address.SubBuilding;
            });
        }

        return {
            TRANSLATION_KEY,
            created,
            isFilled,
            maxCorrespondenceHeight,
            formData,
            cityItemList,
            streetItemList,
            correspondenceCityItemList,
            correspondenceStreetItemList,

            CorrespondenceMethodRef,
            correspondenceMethodArray,
            isPaperCorrespondenceSelected,
            CorrespondenceMethod: CorrespondenceMethodRef,

            countries,
            defaultCountry,
            isDefaultCountrySelected,
            Action,

            blockSendForm,
            keyupEvent,
            expandForm,
            getCityStreets,
            getCorrespondenceStreets,
            requestCorrespondenceCity,
            populateDataForm,
            amplitudeSendEvent,
            AmplitudeAnalyticsEvents,
        };
    },
});
</script>

<style scoped>
.search-instructions {
    font-size: 12px;
    margin-bottom: 5px;
}
</style>
